import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Text,
  Card,
  Loader,
  Icons,
  IconButton,
  Button,
} from "@pushpress/shared-components";
import Dashboard from "../../layouts/Dashboard";
import {
  Badge,
  BadgesService,
  BadgeTemplate,
  GetBadgeProgressResponse,
} from "@pushpress/committed-club-typescript-sdk";
import CommittedClubParticipantsTable from "./CommittedClubParticipantsTable";
import { useHistory, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import * as Ably from "ably";
import { AblyProvider, ChannelProvider, useChannel } from "ably/react";
import styles from "./CommittedClub.module.scss";
import {
  getCommittedClubCheckinChannelName,
  getAblyApiKey,
  copyToClipboard,
  exportMonthProgressToCSV,
  showErrorToast,
} from "./utils";
import { useAnalytics } from "../../components/providers/AnalyticsProvider";
import { CommittedClubEvent } from "./CommittedClubEvents";
import { AppContext } from "../../context/app";

const client = new Ably.Realtime({
  key: getAblyApiKey(),
});

type CommittedClubMonthViewProps = {
  badgeTemplate: BadgeTemplate;
};

type ErrorResponse = { error: string };

const CommittedClubMonthViewWrapper = (props: CommittedClubMonthViewProps) => {
  const { badgeTemplate } = props;
  const app = useContext(AppContext);
  return (
    <AblyProvider client={client}>
      <ChannelProvider
        channelName={getCommittedClubCheckinChannelName(app.client.uuid)}
      >
        <CommittedClubMonthView badgeTemplate={badgeTemplate} />
      </ChannelProvider>
    </AblyProvider>
  );
};

const CommittedClubMonthView = (props: CommittedClubMonthViewProps) => {
  const { badgeTemplate } = props;

  const [loadingProgress, setIsLoadingProgress] = useState(true);
  const [badge, setBadge] = useState<Badge>();
  const [progress, setProgress] = useState<GetBadgeProgressResponse>();

  const app = useContext(AppContext);
  const history = useHistory();
  const { badgeUuid } = useParams<{ badgeUuid: string }>();
  const { trackEvent } = useAnalytics();

  const handleErrorResponse = useCallback(
    (error: ErrorResponse) => {
      showErrorToast(error.error);
      history.replace("/committed-club");
    },
    [history],
  );

  const fetchBadgeProgress = useCallback(() => {
    BadgesService.getBadgeProgress({
      path: { badgeUuid },
      query: { limit: 1000 },
    }).then((response) => {
      if (response.error) {
        return handleErrorResponse(response.error as ErrorResponse);
      }
      setProgress(response.data);
      setIsLoadingProgress(false);
    });
  }, [badgeUuid, handleErrorResponse]);

  useEffect(() => {
    BadgesService.getBadges().then((response) => {
      if (response.error) {
        return handleErrorResponse(response.error as ErrorResponse);
      }
      setBadge(response.data.find((badge) => badge.uuid === badgeUuid));
    });

    fetchBadgeProgress();
  }, [badgeUuid, fetchBadgeProgress, handleErrorResponse, history]);

  useChannel(getCommittedClubCheckinChannelName(app.client.uuid), (message) => {
    const eventName = `app.checkin:${app.client.uuid}`;
    if (message.name === eventName) {
      fetchBadgeProgress();
    }
  });

  const goBack = useCallback(() => {
    history.length > 0 ? history.goBack() : history.push("/committed-club");
  }, [history]);

  const copyNames = useCallback(() => {
    if (!progress) return;

    const names = progress.data
      .filter((user) => user.totalCount >= badge.criteriaTotal)
      .map((user) => user.name)
      .join("\n");
    copyToClipboard(names, "Copied names to clipboard");

    trackEvent(CommittedClubEvent.NamesCopied, {
      page: "month_view",
      month_state: badge.isActive ? "in_progress" : "complete",
      live_now: true,
      app: "committed_club",
    });
  }, [badge, progress, trackEvent]);

  const exportResultsToCSV = useCallback(
    async (source: "table" | "export") => {
      // exportMonthProgressToCSV(badge, progress);
      trackEvent(CommittedClubEvent.ExportAttempted, {
        page: "month_view",
        month_state: badge.isActive ? "in_progress" : "complete",
        live_now: true,
        app: "committed_club",
        source,
      });

      const response = await BadgesService.exportBadgeProgress({
        path: { badgeUuid },
      });

      if (response.error) {
        trackEvent(CommittedClubEvent.ExportFailed, {
          page: "month_view",
          month_state: badge.isActive ? "in_progress" : "complete",
          live_now: true,
          app: "committed_club",
          source,
        });
      } else {
        exportMonthProgressToCSV(response.data as string, badge.title);
        trackEvent(CommittedClubEvent.ExportSuccess, {
          page: "month_view",
          month_state: badge.isActive ? "in_progress" : "complete",
          live_now: true,
          app: "committed_club",
          source,
        });
      }
    },
    [badge, badgeUuid, trackEvent],
  );

  const resultsCard = useMemo(() => {
    if (!badge || badge.isActive || !progress) {
      return null;
    }

    return (
      <>
        <Card>
          <Card.Content>
            <div className="tw-flex tw-flex-col sm:tw-flex-row tw-justify-between tw-gap-6">
              <div>
                <Text variant="body-sm" color="wolf-600">
                  {`${badge.title.toUpperCase()} RESULTS`}
                </Text>
                <div className="tw-h-1" />
                <Text variant="heading-5" color="black">
                  {`${badge.badgesCount} ${
                    badge.badgesCount === 1 ? "person" : "people"
                  } made it into the ${badgeTemplate.title}!`}
                </Text>
              </div>

              <div className="tw-flex tw-gap-3">
                <Button
                  fullWidth
                  text="Copy names"
                  buttonType="secondary"
                  size="medium"
                  iconLocation="left"
                  icon={Icons.Copy}
                  onClick={copyNames}
                />
                <Button
                  fullWidth
                  text="Export"
                  buttonType="secondary"
                  size="medium"
                  iconLocation="left"
                  icon={Icons.Download}
                  onClick={() => exportResultsToCSV("export")}
                />
              </div>
            </div>
          </Card.Content>
        </Card>
        <div className="tw-h-8" />
      </>
    );
  }, [badge, progress, badgeTemplate, copyNames, exportResultsToCSV]);

  if (!badge) {
    return (
      <>
        <Helmet>
          <title>Committed Club</title>
        </Helmet>
        <Dashboard scrollableContent disableContentPadding>
          <div className="tw-flex tw-items-center tw-justify-center tw-w-full tw-h-screen">
            <Loader />
          </div>
        </Dashboard>
      </>
    );
  }

  const month = badge.title.split(" ")[0] ?? "";

  return (
    <>
      <Helmet>
        <title>{badgeTemplate.title}</title>
      </Helmet>
      <Dashboard scrollableContent disableContentPadding>
        <div className={styles.listTitleContainer}>
          <div className="tw-flex tw-items-center tw-space-x-4">
            <IconButton
              active
              icon={Icons.ArrowLeft}
              onClick={goBack}
              size={24}
            />
            <Text weight="600" variant="heading-3" color="black">
              {badgeTemplate.title}
            </Text>
          </div>
        </div>

        <div className={styles.listContainer}>
          <Card>
            <Card.Content>
              <div className="tw-py-4">
                {resultsCard}
                <div className="tw-flex tw-items-center tw-justify-between tw-space-x-4">
                  <Text variant="heading-5" color="black">
                    All {month} Results
                  </Text>
                  <IconButton
                    active
                    tooltip="Export"
                    disabled={loadingProgress}
                    icon={Icons.Download}
                    onClick={() => exportResultsToCSV("table")}
                  />
                </div>
                <div className="tw-h-6" />
                <CommittedClubParticipantsTable
                  badge={badge}
                  data={loadingProgress ? [] : progress?.data}
                  loading={loadingProgress}
                />
                <div className="tw-h-4" />
              </div>
            </Card.Content>
          </Card>
        </div>
      </Dashboard>
    </>
  );
};

export default CommittedClubMonthViewWrapper;
